import { useEffect, useRef, useState } from 'react';
// next
import NextLink from 'next/link';
import { useRouter } from 'next/router';
// config
import { HEADER } from '../../config';

/* ============================================================================
   Menu horizontal do site publico (desktop) - visual novo (shadcn + Tailwind).

   Substitui `Stack` + `Link` estilizado + `Popover` + `Grid`/`List`/
   `ListSubheader`/`CardActionArea` do MUI. As props continuam as mesmas
   (`isOffset`, `isHome`, `navConfig`), entao o MainHeader nao precisou mudar de
   contrato.

   O QUE CONTINUA IGUAL: item com `children` abre um painel abaixo do cabecalho
   com os grupos (`subheader` + `items`); "Documentation" abre em nova aba; o item
   da rota atual fica destacado; o painel fecha ao trocar de rota; existe UM
   estado de aberto para todos os itens (so um painel por vez), como no original.

   O QUE MUDOU, e por que:

   1. O painel de "Pages" mostrava uma IMAGEM vinda de
      minimal-assets-api.vercel.app (illustration_dashboard.png) no lugar do texto
      do item "Dashboard". Era asset externo do template - se o dominio sai do ar,
      o item vira um retangulo vazio. Agora o item e um link com texto, como os
      outros.

   2. GANHOU fechar ao clicar fora e fechar com Esc, e o gatilho virou <button>
      com `aria-expanded`. O `Popover` do MUI dava isso de graca; feito na mao
      precisa ser escrito - sem isso o painel ficava por cima da pagina ate a
      pessoa navegar. Antes o gatilho era um <a> sem href, invisivel para teclado.

   3. `isHome` nao pinta mais o texto de branco (mesmo motivo explicado no
      MainHeader: `/` nao tem mais foto escura atras, texto branco desapareceria).
      A prop continua aceita para nao mexer em quem chama.
   ============================================================================ */

const CLASSES_LINK = 'text-[12.5px] font-semibold text-ink no-underline transition-colors hover:text-primary';

export default function MenuDesktop({ isOffset, isHome, navConfig }) {
  const { pathname } = useRouter();
  const [aberto, setAberto] = useState(false);
  const raiz = useRef(null);

  // Fecha ao navegar: o painel nao pode continuar por cima da tela nova.
  useEffect(() => {
    setAberto(false);
  }, [pathname]);

  // Fecha ao clicar fora e com Esc. Usa mousedown (nao click) porque um mousedown
  // fora seguido de mouseup dentro do painel deixava-o aberto.
  useEffect(() => {
    if (!aberto) return;
    const aoApontar = (e) => {
      if (raiz.current && !raiz.current.contains(e.target)) setAberto(false);
    };
    const aoTeclar = (e) => e.key === 'Escape' && setAberto(false);
    document.addEventListener('mousedown', aoApontar);
    window.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoApontar);
      window.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto]);

  return (
    <nav ref={raiz} className="flex items-center gap-6" aria-label="Menu principal">
      {navConfig.map((link) => (
        <MenuDesktopItem
          key={link.title}
          item={link}
          isOpen={aberto}
          onOpen={() => setAberto(true)}
          onClose={() => setAberto(false)}
          isOffset={isOffset}
          isHome={isHome}
        />
      ))}
    </nav>
  );
}

// ----------------------------------------------------------------------------

function MenuDesktopItem({ item, isOffset, isOpen, onOpen, onClose }) {
  const { pathname } = useRouter();

  const { title, path, children } = item;

  const isActive = (p) => pathname === p;

  if (children) {
    return (
      <div className="relative">
        <button
          type="button"
          onClick={isOpen ? onClose : onOpen}
          aria-expanded={isOpen}
          className={`flex cursor-pointer items-center gap-1 bg-transparent ${CLASSES_LINK}`}
        >
          {title}
          <span aria-hidden className="text-[10px] text-faint">
            {isOpen ? '▲' : '▼'}
          </span>
        </button>

        {isOpen && (
          // O painel e ancorado ao CABECALHO, nao ao botao: tem varias colunas e,
          // preso ao botao, sairia da tela pela direita. A altura do topo segue a
          // mesma constante que o MainHeader usa, entao ele acompanha a faixa
          // quando ela encolhe ao rolar.
          <div
            className="fixed left-0 right-0 z-[1200] px-4 lg:px-6"
            style={{ top: isOffset ? HEADER.MOBILE_HEIGHT : HEADER.MAIN_DESKTOP_HEIGHT }}
          >
            <div className="mx-auto grid max-w-[1200px] grid-cols-2 gap-6 rounded-lg border border-line bg-surface p-6 shadow-card md:grid-cols-4">
              {children.map((list) => (
                <div key={list.subheader}>
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
                    {list.subheader}
                  </p>
                  <ul className="space-y-1.5">
                    {list.items.map((sub) => (
                      <li key={sub.title}>
                        <NextLink href={sub.path} passHref>
                          <span
                            onClick={onClose}
                            className={`block cursor-pointer text-[12.5px] no-underline transition-colors hover:text-ink ${
                              isActive(sub.path) ? 'font-semibold text-ink' : 'text-muted-foreground'
                            }`}
                          >
                            {sub.title}
                          </span>
                        </NextLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // "Documentation" e link externo: fica fora do react-router de proposito.
  if (title === 'Documentation') {
    return (
      <a href={path} target="_blank" rel="noopener noreferrer" className={CLASSES_LINK}>
        {title}
      </a>
    );
  }

  return (
    <NextLink href={path} passHref>
      <span className={`cursor-pointer ${CLASSES_LINK} ${isActive(path) ? 'text-primary' : ''}`}>{title}</span>
    </NextLink>
  );
}
